import React, { useState } from 'react';
import { Modal, StyleSheet, useWindowDimensions, Platform } from 'react-native';
import {
  Box,
  View,
  Center,
  Card,
  Spinner,
  Text,
  Heading,
  HStack,
  VStack,
  useToken,
  Pressable,
  ScrollView,
  Icon,
} from '@gluestack-ui/themed';
import { X, ChevronDown, ChevronUp } from 'lucide-react-native';
import useModuleData from '../../../common/hooks/student/useModuleData';
import HtmlText from '../../../common/components/HtmlText';
import { Divider } from '@gluestack-ui/themed';

const ModuleInfoElement = ({ title, children }) => (
  <Card p="$8" testID={title.toLowerCase().replace(/\s+/g, '-')}>
    <Heading size="sm" mb="$3">
      {title}
    </Heading>
    {children}
  </Card>
);

// Simple static progress bar component
const StaticProgressBar = ({ progress, color }) => (
  <Box w="100%" h="$1" bg="$backgroundLight200" mt="$1" mb="$5">
    <Box w={`${progress * 100}%`} h="100%" bg={color} />
  </Box>
);

const TransferTaskEvaluationCriteria = ({ grading }) => {
  const blueColor = useToken('colors', 'blue500');

  return (
    <ModuleInfoElement title="Evaluation of Transfer Tasks">
      {grading?.scores &&
        Object.entries(grading?.scores)
          .slice(0, 7)
          .map(([outer_key, { key, label, min, max, score }]) => (
            <View key={key}>
              <HStack>
                <Text size="xs" alignSelf="flex-start" flex={1} mr="$2">
                  {label}
                </Text>
                <Text size="xs" textAlign="right" alignSelf="flex-end">
                  {`${score == -99 ? '-' : score} / ${max}`}
                </Text>
              </HStack>
              <StaticProgressBar progress={score / max} color={blueColor} />
            </View>
          ))}
    </ModuleInfoElement>
  );
};

const RegularTaskEvaluationCriteria = ({ grading }) => {
  const blueColor = useToken('colors', 'blue500');

  return (
    <ModuleInfoElement title="Evaluation of Regular Tasks">
      {grading?.scores &&
        Object.entries(grading?.scores)
          .slice(7)
          .map(([outer_key, { key, label, min, max, score }]) => (
            <View key={key}>
              <View style={{ flexDirection: 'row' }}>
                <Text size="xs" alignSelf="flex-start" flex={1} mr="$2">
                  {label}
                </Text>
                <Text size="xs" textAlign="right" alignSelf="flex-end">
                  {`${score == -99 ? '-' : score} / ${max}`}
                </Text>
              </View>
              <StaticProgressBar progress={score / max} color={blueColor} />
            </View>
          ))}
    </ModuleInfoElement>
  );
};

const OverallGrade = ({ grading }) => (
  <ModuleInfoElement title="Grade">
    <Text size="xl">{grading?.grade ? grading.grade.toFixed(1) : '-'}</Text>
  </ModuleInfoElement>
);

const ModuleFeedback = ({ grading }) => (
  <ModuleInfoElement title="Module Feedback">
    <HtmlText content={grading?.comment || ''} />
  </ModuleInfoElement>
);

const TaskFeedback = ({ tasks }) => {
  const { width } = useWindowDimensions();
  const levels = [1, 2, 3];

  const [showFullText, setShowFullText] = useState({});
  const [openAccordion, setOpenAccordion] = useState(null);

  const [showTaskResponseModal, setShowTaskResponseModal] = useState({});

  const toggleTaskResponseModal = (id) => {
    setShowTaskResponseModal((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleAccordionPress = (id) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const toggleFullText = (id) => {
    setShowFullText((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const feedbackCutOff = 400;

  return (
    <ModuleInfoElement title="Individual Task Feedback">
      {levels.map((levelId) => (
        <View key={`level-${levelId}`}>
          {tasks
            ?.filter((task) => task.level === levelId)
            .map((task) => (
              <View key={`task-${task.id}`}>
                <Pressable onPress={() => handleAccordionPress(task.id)} accessibilityRole="button">
                  <HStack justifyContent="space-between" alignItems="center" py="$2">
                    <Text size="sm">{task.title}</Text>
                    <Icon as={openAccordion === task.id ? ChevronUp : ChevronDown} size="lg" color="$textLight800" />
                  </HStack>
                </Pressable>
                {openAccordion === task.id && (
                  <View ml="$4" my="$2">
                    {task.submission?.legacyFeedback ? (
                      <HtmlText
                        content={
                          Platform.OS == 'web' || showFullText[task.id]
                            ? task.submission.legacyFeedback
                            : task.submission.legacyFeedback.substring(0, feedbackCutOff) + '...'
                        }
                      />
                    ) : (
                      <Text size="xs">No feedback</Text>
                    )}

                    <View style={{ flexDirection: 'row' }}>
                      {Platform.OS !== 'web' && task.feedback?.length > feedbackCutOff && (
                        <Text
                          style={{ color: useToken('colors', 'blue500'), marginRight: 20 }}
                          onPress={() => toggleFullText(task.id)}
                        >
                          {showFullText[task.id] ? 'Collapse full feedback' : 'Show full feedback'}
                        </Text>
                      )}
                      <Pressable
                        onPress={() => {
                          toggleTaskResponseModal(task.id);
                        }}
                      >
                        <Text size="sm" style={{ color: useToken('colors', 'blue500') }}>
                          Show task and my response
                        </Text>
                      </Pressable>

                      <Modal visible={!!showTaskResponseModal[task.id]} transparent>
                        <View
                          style={{
                            // this is the backdrop
                            flex: 1,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)', // This is your backdrop
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <View
                            bg="white"
                            $base-w="90%"
                            $lg-w="60%"
                            maxWidth={1000}
                            flex={1}
                            overflow="scroll"
                            pt="$6"
                            px="$6"
                            my="$4"
                          >
                            <HStack justifyContent="space-between" alignItems="center" mb="$4">
                              <Heading mb="$2" size="md">
                                {task.title}
                              </Heading>
                              <Pressable
                                testID="close-modal"
                                onPress={() => {
                                  toggleTaskResponseModal(task.id);
                                }}
                                accessibilityRole="button"
                              >
                                <Icon as={X} size="lg" color="$textLight800" />
                              </Pressable>
                            </HStack>
                            <ScrollView flex={1} overflow="scroll" pb="$2" pr="$4">
                              <HtmlText content={task.description} />
                              <Divider mb="$6" />
                              <HtmlText
                                content={task.submission?.text || 'Empty response'}
                                applySubmissionFormatting={true}
                              />
                            </ScrollView>
                          </View>
                        </View>
                      </Modal>
                    </View>
                  </View>
                )}
              </View>
            ))}
        </View>
      ))}
    </ModuleInfoElement>
  );
};

const LegacyGradeReport = ({ module, tasks, isLoading }) => {
  if (!module) {
    return (
      <Center>
        <Spinner />
      </Center>
    );
  }

  const { grading } = module;

  return (
    <VStack space="md" mt="$6">
      <Heading>
        Grades for '{module.title}' (v{module.version})
      </Heading>
      <HStack $base-flexDirection="column" $lg-flexDirection="row" space="md">
        <Box minWidth={120} $lg-flex={1}>
          <OverallGrade grading={grading} />
        </Box>
        <Box $base-flex={1} $lg-flex={5}>
          <ModuleFeedback grading={grading} />
        </Box>
      </HStack>
      <VStack $base-flexDirection="column" $lg-flexDirection="row" space="md">
        <Box $lg-flex={1} py="$2">
          <TransferTaskEvaluationCriteria testID="transfer-task-evaluation-criteria" grading={grading} />
        </Box>
        <Box $lg-flex={1} py="$2">
          <RegularTaskEvaluationCriteria testID="regular-task-evaluation-criteria" grading={grading} />
        </Box>
      </VStack>
      <VStack space="md" mt="$4">
        {!isLoading && !!tasks ? <TaskFeedback testID="task-feedback" tasks={tasks} /> : <Spinner />}
      </VStack>
    </VStack>
  );
};

const styles = StyleSheet.create({
  progressBar: {
    marginVertical: 10,
  },
  modalTitle: { fontSize: 18, fontWeight: 'bold', marginBottom: 10 },
  modalDescription: { fontSize: 16, marginBottom: 10 },
  modalSubmission: { fontSize: 14, color: 'gray' },
});

export default LegacyGradeReport;
